import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import HtmlDocumentView from './HtmlDocumentView';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { QMS_DOCS_TOUR_ID, qmsDocsTourSteps } from './qmsDocsTour';
import { QMS_DOC_TYPES, docTypePlural } from '../../utils/qmsDocTypes';

const CATEGORIES = [
  { key: 'quality',       label: 'Quality' },
  { key: 'environmental', label: 'Environmental' },
  { key: 'security',      label: 'Security' },
  { key: 'general',       label: 'General' },
];

const KNOWN_ACCREDITATIONS = [
  { type: 'iso_9001',               label: 'ISO 9001',            sub: 'Quality Management' },
  { type: 'iso_27001',              label: 'ISO 27001',           sub: 'Information Security' },
  { type: 'iso_14001',              label: 'ISO 14001',           sub: 'Environmental' },
  { type: 'constructionline_gold',  label: 'Constructionline',    sub: 'Gold' },
  { type: 'safecontractor',         label: 'SafeContractor',      sub: 'H&S Accreditation' },
];

const ACC_COLORS = {
  iso_9001:              'bg-blue-100 text-blue-700',
  iso_27001:             'bg-purple-100 text-purple-700',
  iso_14001:             'bg-green-100 text-green-700',
  constructionline_gold: 'bg-amber-100 text-amber-700',
  safecontractor:        'bg-orange-100 text-orange-700',
};

function accreditationStatusColor(status) {
  if (status === 'expired') return { badge: 'bg-red-100 text-red-700', dot: 'bg-red-500', border: 'border-red-200' };
  if (status === 'due_soon') return { badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400', border: 'border-amber-200' };
  return { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-200' };
}

function AccreditationCard({ acc, meta }) {
  if (!acc) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col gap-1.5">
        <span className="font-bold text-[13px] text-gray-400 leading-tight">{meta.label}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 font-semibold uppercase w-fit">Not configured</span>
        <div className="text-[10px] text-gray-400">{meta.sub}</div>
      </div>
    );
  }
  const colors = accreditationStatusColor(acc.computed_status);
  return (
    <div className={`bg-white border ${colors.border} rounded-xl p-4 flex flex-col gap-2`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
            <span className="font-bold text-[13px] text-navy">{acc.name}</span>
          </div>
          {acc.certifying_body && <div className="text-[10px] text-gray-500 mt-0.5 pl-4">{acc.certifying_body}</div>}
        </div>
        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase flex-shrink-0 ${colors.badge}`}>
          {acc.computed_status === 'due_soon' ? 'Due Soon' : acc.computed_status === 'expired' ? 'Expired' : 'Current'}
        </span>
      </div>
      {acc.scope && <div className="text-[10px] text-gray-500 leading-relaxed line-clamp-2">{acc.scope}</div>}
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9px] text-gray-500">
        {acc.expiry_date && (
          <>
            <span className="font-semibold text-gray-400">Expires</span>
            <span className={acc.computed_status !== 'current' ? 'font-semibold text-amber-600' : ''}>
              {formatDate(acc.expiry_date)}
              {acc.days_to_expiry !== null && acc.computed_status !== 'expired' && (
                <span className="text-gray-400"> ({acc.days_to_expiry}d)</span>
              )}
            </span>
          </>
        )}
        {acc.next_audit_date && (
          <>
            <span className="font-semibold text-gray-400">Next Audit</span>
            <span>{formatDate(acc.next_audit_date)}</span>
          </>
        )}
        {acc.responsible_owner_name && (
          <>
            <span className="font-semibold text-gray-400">Owner</span>
            <span>{acc.responsible_owner_name}</span>
          </>
        )}
      </div>
      {acc.certificate_r2_key && (
        <a
          href={`/api/uploads/${acc.certificate_r2_key}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[9px] text-gold hover:text-gold/80 font-semibold flex items-center gap-1 mt-1"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          View Certificate
        </a>
      )}
    </div>
  );
}

// Renders a PDF as canvases and fires onBottom when user reaches the last page
function PdfScrollViewer({ url, onBottom }) {
  const scrollRef        = useRef(null);
  const canvasRef        = useRef(null);
  const sentinelRef      = useRef(null);
  const onBottomRef      = useRef(onBottom);
  const [phase, setPhase]       = useState('loading'); // loading | rendering | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Keep the ref current without re-running the observer effect
  onBottomRef.current = onBottom;

  useEffect(() => {
    if (!url) return;
    let cancelled = false;

    async function render() {
      try {
        const loadTask = pdfjsLib.getDocument({ url });
        const pdf = await loadTask.promise;
        if (cancelled) return;

        const total = pdf.numPages;
        setPhase('rendering');
        setProgress({ done: 0, total });

        const container = canvasRef.current;
        if (!container) return;
        container.innerHTML = '';

        const containerWidth = container.offsetWidth || 680;

        for (let i = 1; i <= total; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = containerWidth / base.width;
          const vp = page.getViewport({ scale });
          const dpr = Math.min(window.devicePixelRatio || 1, 2); // cap at 2x to save memory

          const canvas = document.createElement('canvas');
          canvas.width  = Math.floor(vp.width  * dpr);
          canvas.height = Math.floor(vp.height * dpr);
          canvas.style.cssText = `width:100%;display:block;background:#fff;${i < total ? 'margin-bottom:4px;' : ''}`;
          container.appendChild(canvas);

          const ctx = canvas.getContext('2d');
          ctx.scale(dpr, dpr);
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
          if (cancelled) return;
          setProgress({ done: i, total });
        }

        if (!cancelled) setPhase('done');
      } catch (err) {
        console.error('[PdfScrollViewer] render failed:', err);
        if (!cancelled) setPhase('error');
      }
    }

    render();
    return () => { cancelled = true; };
  }, [url]);

  // Wire up IntersectionObserver once all pages are rendered.
  // Deliberately omits onBottom from deps — the ref keeps it current so the
  // observer never needs to be recreated due to callback identity changes,
  // which previously caused it to re-fire on every re-render.
  useEffect(() => {
    if (phase !== 'done' || !sentinelRef.current || !scrollRef.current) return;
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { onBottomRef.current(); obs.disconnect(); } },
      { root, threshold: 0.1 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#525659]" style={{ minHeight: 0 }}>
      <div className="p-2 sm:p-3">
        <div ref={canvasRef} />

        {phase === 'loading' && (
          <div className="flex items-center justify-center h-64 gap-2">
            <svg className="w-4 h-4 animate-spin text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
            <span className="text-[11px] text-white/50">Loading document…</span>
          </div>
        )}

        {phase === 'rendering' && progress.done < progress.total && (
          <div className="text-center pt-2 pb-1">
            <span className="text-[9px] text-white/40">Rendering page {progress.done} of {progress.total}…</span>
          </div>
        )}

        {phase === 'error' && (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <p className="text-[11px] text-white/60">Unable to render PDF in browser.</p>
            <a href={url} target="_blank" rel="noopener noreferrer"
               className="text-[11px] text-gold font-semibold underline">
              Open PDF in new tab ↗
            </a>
          </div>
        )}
      </div>

      {/* Sentinel — entering viewport means user reached the bottom */}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  );
}

// Reads a document: a PDF version in the pdf.js viewer, an HTML version in
// HtmlDocumentView. When the document requires acknowledgement (ackRequired),
// the sidebar carries the read-to-the-bottom rules; otherwise it is a reader
// with a Close button. Exported because the Actions card opens the same reader
// for an acknowledgement action, so the rules live here once.
export function AcknowledgeModal({ doc, onClose, onAcknowledged, ackRequired = true }) {
  const [scrolled,  setScrolled]  = useState(false);
  const [autoAcked, setAutoAcked] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const { toast } = useToast();

  const canConfirm = scrolled && confirmed;
  const isHtml = doc.content_kind === 'html';
  const url = !isHtml && doc.r2_key ? `/api/uploads/${doc.r2_key}` : null;

  // Auto-acknowledge as soon as the user reads to the bottom.
  // onAcknowledged is intentionally NOT called here — it's called when the user
  // clicks Done. Calling it here would update the parent's docs state, which in
  // the Outstanding view removes this row from the filter and unmounts the modal.
  const handleBottom = useCallback(async () => {
    setScrolled(true);
    if (!ackRequired) return;
    setSaving(true);
    try {
      await api.post(`qms/documents/${doc.id}/acknowledge`, {});
      setAutoAcked(true);
      toast('Document acknowledged ✓', 'success');
    } catch {
      // silent — user can still manually confirm below
    } finally {
      setSaving(false);
    }
  }, [doc.id, toast, ackRequired]);

  const handleSubmit = async () => {
    if (autoAcked) { onAcknowledged(doc.id); onClose(); return; }
    if (!canConfirm) return;
    setSaving(true);
    setError('');
    try {
      await api.post(`qms/documents/${doc.id}/acknowledge`, {});
      toast('Document acknowledged', 'success');
      onAcknowledged(doc.id);
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to acknowledge');
    } finally {
      setSaving(false);
    }
  };

  const categoryLabel = CATEGORIES.find(c => c.key === doc.category)?.label || doc.category || '';

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col sm:items-center sm:justify-center bg-black/70 sm:p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-5xl flex flex-col flex-1 sm:flex-none sm:h-[95dvh] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0 bg-navy">
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[15px] text-white leading-tight truncate">{doc.title}</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[9px] font-mono bg-white/20 text-white/90 px-1.5 py-0.5 rounded">{doc.doc_number}</span>
              {doc.version_number && <span className="text-[9px] text-white/60">v{doc.version_number}</span>}
              {categoryLabel && <span className="text-[9px] text-white/60 capitalize">{categoryLabel}</span>}
            </div>
          </div>
          <button onClick={onClose} className="flex-shrink-0 text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ── Body: PDF + sidebar ─────────────────────────────────── */}
        <div className="flex flex-1 min-h-0">

          {/* Document viewer: HTML content or the PDF */}
          <div className="flex-1 min-w-0 flex flex-col">
            {isHtml ? (
              <HtmlDocumentView doc={doc} versionId={doc.current_version_id} onBottom={handleBottom} />
            ) : url ? (
              <PdfScrollViewer url={url} onBottom={handleBottom} />
            ) : (
              <div className="flex-1 flex items-center justify-center text-[11px] text-gray-400">
                No PDF attached to this document version.
              </div>
            )}
          </div>

          {/* ── Right sidebar (desktop only) ────────────────────── */}
          <div className="hidden sm:flex flex-col w-56 flex-shrink-0 border-l border-gray-100 bg-gray-50">

            {/* Doc metadata */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mb-1">Document</div>
                <div className="font-mono text-[12px] font-bold text-navy">{doc.doc_number}</div>
                {doc.version_number && (
                  <div className="text-[10px] text-gray-500 mt-0.5">Version {doc.version_number}</div>
                )}
                {categoryLabel && (
                  <div className="text-[9px] text-gray-400 mt-0.5 capitalize">{categoryLabel}</div>
                )}
              </div>

              {doc.owner_name && (
                <div>
                  <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mb-1">Document Owner</div>
                  <div className="text-[10px] text-gray-700">{doc.owner_name}</div>
                </div>
              )}

              {doc.next_review_date && (
                <div>
                  <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mb-1">Next Review</div>
                  <div className="text-[10px] text-gray-700">{formatDate(doc.next_review_date)}</div>
                </div>
              )}

              {ackRequired && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="text-[9px] font-bold text-amber-800 mb-0.5">Mandatory Reading</div>
                <div className="text-[8px] text-amber-700 leading-relaxed">
                  You are required to read {isHtml ? 'the full version of ' : ''}this document and confirm your understanding.
                </div>
              </div>
              )}

              {/* Scroll status */}
              {ackRequired && (
              <div className={`rounded-xl p-3 border transition-colors duration-300 ${autoAcked ? 'bg-emerald-50 border-emerald-300' : scrolled ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-100 border-gray-200'}`}>
                {autoAcked ? (
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span className="text-[9px] font-bold text-emerald-700">Document acknowledged ✓</span>
                  </div>
                ) : scrolled ? (
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span className="text-[9px] font-bold text-emerald-700">Document fully viewed</span>
                  </div>
                ) : (
                  <div className="text-[9px] text-gray-500 text-center leading-relaxed">
                    Scroll to the bottom of the {isHtml ? 'full version' : 'document'} to unlock acknowledgement ↓
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Confirm + action */}
            <div className="border-t border-gray-200 p-4 space-y-3 bg-white">
              {!ackRequired ? (
                <button
                  onClick={onClose}
                  className="w-full py-2.5 bg-navy text-white text-[11px] font-bold rounded-xl hover:bg-navy/80 transition-colors"
                >
                  Close
                </button>
              ) : autoAcked ? (
                <button
                  onClick={() => { onAcknowledged(doc.id); onClose(); }}
                  className="w-full py-2.5 bg-emerald-600 text-white text-[11px] font-bold rounded-xl hover:bg-emerald-700 transition-colors"
                >
                  Done
                </button>
              ) : (
                <>
                  <div className={`transition-opacity duration-300 ${scrolled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                    <label className="flex items-start gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={e => setConfirmed(e.target.checked)}
                        disabled={!scrolled}
                        className="mt-0.5 w-4 h-4 accent-navy flex-shrink-0"
                      />
                      <span className="text-[12px] text-gray-900 leading-relaxed font-medium">
                        I confirm I have read and understood this document
                        {doc.version_number && ` (v${doc.version_number})`} and agree to comply with its requirements.
                      </span>
                    </label>
                  </div>
                  {error && <div className="text-[9px] text-red-600 bg-red-50 border border-red-100 px-2 py-1.5 rounded-lg">{error}</div>}
                  <button
                    onClick={handleSubmit}
                    disabled={!canConfirm || saving}
                    className="w-full py-2.5 bg-navy text-white text-[11px] font-bold rounded-xl hover:bg-navy/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Saving…' : 'Acknowledge Document'}
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full py-1.5 border border-gray-200 text-[10px] text-gray-500 rounded-xl hover:border-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Mobile footer ───────────────────────────────────────── */}
        <div className="sm:hidden flex-shrink-0 border-t-2 border-gray-200 px-4 py-3 bg-white">
          {!ackRequired ? (
            <button onClick={onClose} className="w-full py-2.5 bg-navy text-white text-[11px] font-bold rounded-xl hover:bg-navy/80 transition-colors">
              Close
            </button>
          ) : autoAcked ? (
            <button onClick={() => { onAcknowledged(doc.id); onClose(); }} className="w-full py-2.5 bg-emerald-600 text-white text-[11px] font-bold rounded-xl hover:bg-emerald-700 transition-colors">
              Done ✓
            </button>
          ) : (
            <>
              {!scrolled && (
                <div className="text-[9px] text-amber-600 font-semibold text-center mb-2">
                  ↓ Scroll to the bottom of the document to unlock
                </div>
              )}
              <div className={`flex items-start gap-2 mb-2 transition-opacity duration-300 ${scrolled ? 'opacity-100' : 'opacity-35 pointer-events-none'}`}>
                <input
                  id="mob-ack"
                  type="checkbox"
                  checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                  disabled={!scrolled}
                  className="mt-0.5 w-4 h-4 accent-navy flex-shrink-0"
                />
                <label htmlFor="mob-ack" className="text-[10px] text-gray-700 leading-snug cursor-pointer select-none">
                  I confirm I have read and understood this document and agree to comply.
                </label>
              </div>
              {error && <div className="text-[9px] text-red-600 mb-2">{error}</div>}
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-2 border border-gray-200 text-[10px] text-gray-600 rounded-xl hover:border-gray-300 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canConfirm || saving}
                  className="flex-1 py-2 bg-navy text-white text-[11px] font-bold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed hover:bg-navy/80 transition-colors"
                >
                  {saving ? 'Saving…' : 'Acknowledge Document'}
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
}

function DocumentRow({ doc, accTypes, onAcknowledge }) {
  const [expanded, setExpanded]     = useState(false);
  const [showAckModal, setShowAckModal] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const isLive = doc.status === 'live';
  const isHtml = doc.content_kind === 'html';
  const canDownload = isLive && !isHtml && doc.r2_key;
  const canRead = isLive && (isHtml || doc.r2_key);
  const needsAck = doc.requires_acknowledgement && isLive && !doc.my_read;
  const openReader = (e, forAck) => { e.stopPropagation(); setReadOnly(!forAck); setShowAckModal(true); };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white hover:border-navy/30 transition-colors">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(v => !v)}
      >
        {/* Doc number */}
        <span className="text-[8px] font-bold bg-navy/10 text-navy px-1.5 py-0.5 rounded font-mono flex-shrink-0">
          {doc.doc_number}
        </span>

        {/* Title and meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[12px] text-navy">{doc.title}</span>
            {doc.version_number && (
              <span className="text-[8px] text-gray-400 font-mono">v{doc.version_number}</span>
            )}
            {isHtml && !!doc.has_overview && (
              <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700" title="A short plain-English overview is available alongside the full version">
                Overview
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[9px] text-gray-400 flex-wrap">
            {doc.owner_name && <span>Owner: {doc.owner_name}</span>}
            {doc.next_review_date && <span>Review: {formatDate(doc.next_review_date)}</span>}
            {doc.description && (
              <span className="truncate max-w-xs">{doc.description}</span>
            )}
          </div>
          {accTypes.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {accTypes.map(t => {
                const meta = KNOWN_ACCREDITATIONS.find(a => a.type === t);
                return meta ? (
                  <span key={t} className={`text-[7px] font-bold px-1.5 py-0.5 rounded-full ${ACC_COLORS[t] || 'bg-gray-100 text-gray-500'}`}>
                    {meta.label}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>

        {/* Read status */}
        <div className="flex-shrink-0 text-right flex items-center justify-end gap-1.5">
          {doc.requires_acknowledgement && isLive ? (
            doc.my_read ? (
              <>
                {canRead && (
                  <button
                    onClick={e => openReader(e, false)}
                    className="text-[9px] text-navy border border-gray-200 hover:border-navy px-2 py-0.5 rounded-full font-semibold transition-colors"
                  >
                    Read
                  </button>
                )}
                <span className="inline-flex items-center gap-1 text-[9px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Acknowledged
                </span>
              </>
            ) : (
              <button
                onClick={e => openReader(e, true)}
                className="text-[9px] bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded-full font-semibold transition-colors"
                data-tour="qms-read-acknowledge"
              >
                Read &amp; Acknowledge
              </button>
            )
          ) : canRead ? (
            <button
              onClick={e => openReader(e, false)}
              className="text-[9px] text-navy border border-gray-200 hover:border-navy px-2 py-0.5 rounded-full font-semibold transition-colors"
              data-tour="qms-read"
            >
              Read
            </button>
          ) : (
            <span className="text-[9px] text-gray-300">—</span>
          )}
        </div>

        {/* Expand chevron */}
        <svg
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-2">
          {doc.description && (
            <p className="text-[10px] text-gray-600 leading-relaxed">{doc.description}</p>
          )}
          {doc.change_summary && (
            <div className="text-[9px] text-gray-500">
              <span className="font-semibold text-gray-400">Change notes: </span>{doc.change_summary}
            </div>
          )}
          <div className="flex items-center gap-3 pt-1">
            {canDownload && (
              <a
                href={`/api/uploads/${doc.r2_key}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[9px] bg-navy text-white px-2.5 py-1 rounded font-semibold hover:bg-navy/80 transition-colors"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download
              </a>
            )}
            {doc.my_read_at && (
              <span className="text-[9px] text-gray-400">Acknowledged {formatDate(doc.my_read_at)}</span>
            )}
          </div>
        </div>
      )}
      {showAckModal && (
        <AcknowledgeModal
          doc={doc}
          ackRequired={!readOnly && needsAck}
          onClose={() => setShowAckModal(false)}
          onAcknowledged={id => onAcknowledge(id)}
        />
      )}
    </div>
  );
}

function MatrixView({ docs, formatDate: fmtDate }) {
  const [accFilter, setAccFilter] = useState('all');

  const today = new Date();
  const soon = new Date(today);
  soon.setDate(soon.getDate() + 90);

  const filteredDocs = useMemo(() => {
    const sorted = [...docs].sort((a, b) => {
      const catA = a.category || '';
      const catB = b.category || '';
      if (catA !== catB) return catA.localeCompare(catB);
      return (a.doc_number || '').localeCompare(b.doc_number || '');
    });
    if (accFilter === 'all') return sorted;
    return sorted.filter(doc => {
      const types = (doc.accreditation_types || '').split(',').filter(Boolean);
      return types.includes(accFilter);
    });
  }, [docs, accFilter]);

  // Group by category preserving sort order
  const groupedByCategory = useMemo(() => {
    const groups = [];
    const seen = {};
    for (const doc of filteredDocs) {
      const cat = doc.category || 'general';
      if (!seen[cat]) {
        seen[cat] = true;
        groups.push({ key: cat, docs: [] });
      }
      groups[groups.length - 1].docs.push(doc);
    }
    return groups;
  }, [filteredDocs]);

  function nextReviewClass(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (d < today) return 'text-red-600 font-semibold';
    if (d <= soon) return 'text-amber-600 font-semibold';
    return '';
  }

  const categoryLabel = (key) => CATEGORIES.find(c => c.key === key)?.label || key;

  return (
    <div className="space-y-3">
      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setAccFilter('all')}
          className={`text-[9px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
            accFilter === 'all'
              ? 'bg-navy text-white border-navy'
              : 'border-gray-300 text-gray-500 hover:border-navy hover:text-navy'
          }`}
        >
          All
        </button>
        {KNOWN_ACCREDITATIONS.map(acc => (
          <button
            key={acc.type}
            onClick={() => setAccFilter(acc.type)}
            className={`text-[9px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
              accFilter === acc.type
                ? `${ACC_COLORS[acc.type]} border-current`
                : 'border-gray-300 text-gray-500 hover:border-navy hover:text-navy'
            }`}
          >
            {acc.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-[9px] border-collapse">
          <thead>
            <tr className="bg-gray-50 sticky top-0 z-10">
              <th className="text-left font-bold text-gray-500 uppercase tracking-wider px-3 py-2 border-b border-gray-200 whitespace-nowrap">Doc #</th>
              <th className="text-left font-bold text-gray-500 uppercase tracking-wider px-3 py-2 border-b border-gray-200">Title</th>
              <th className="text-left font-bold text-gray-500 uppercase tracking-wider px-3 py-2 border-b border-gray-200 whitespace-nowrap">Review</th>
              <th className="text-left font-bold text-gray-500 uppercase tracking-wider px-3 py-2 border-b border-gray-200 whitespace-nowrap">Next Review</th>
              {KNOWN_ACCREDITATIONS.map(acc => (
                <th
                  key={acc.type}
                  className="text-center font-bold text-gray-500 uppercase tracking-wider px-3 py-2 border-b border-gray-200 whitespace-nowrap"
                >
                  {acc.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupedByCategory.length === 0 ? (
              <tr>
                <td colSpan={4 + KNOWN_ACCREDITATIONS.length} className="text-center text-gray-400 py-8 text-[10px]">
                  No documents match this filter.
                </td>
              </tr>
            ) : (
              groupedByCategory.map(group => (
                <>
                  <tr key={`cat-${group.key}`} className="bg-gray-100">
                    <td
                      colSpan={4 + KNOWN_ACCREDITATIONS.length}
                      className="px-3 py-1.5 text-[8px] font-bold text-gray-500 uppercase tracking-widest border-b border-gray-200"
                    >
                      {categoryLabel(group.key)}
                    </td>
                  </tr>
                  {group.docs.map((doc, idx) => {
                    const accTypes = (doc.accreditation_types || '').split(',').filter(Boolean);
                    return (
                      <tr
                        key={doc.id}
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${idx % 2 === 1 ? 'bg-white' : 'bg-white'}`}
                      >
                        <td className="px-3 py-2 font-mono text-navy whitespace-nowrap">
                          {doc.doc_number}
                        </td>
                        <td className="px-3 py-2 text-navy font-semibold text-[10px] min-w-[200px]">
                          {doc.title}
                        </td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                          {doc.review_period_months ? `${doc.review_period_months}m` : '—'}
                        </td>
                        <td className={`px-3 py-2 whitespace-nowrap ${nextReviewClass(doc.next_review_date)}`}>
                          {doc.next_review_date ? fmtDate(doc.next_review_date) : '—'}
                        </td>
                        {KNOWN_ACCREDITATIONS.map(acc => (
                          <td key={acc.type} className="px-3 py-2 text-center">
                            {accTypes.includes(acc.type) ? (
                              <span className={`inline-block text-[7px] font-bold px-1.5 py-0.5 rounded-full ${ACC_COLORS[acc.type] || 'bg-gray-100 text-gray-500'}`}>
                                ✓
                              </span>
                            ) : null}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function QmsLibraryPage() {
  const [docs, setDocs] = useState([]);
  const [accreditations, setAccreditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('library');
  const [showOutstanding, setShowOutstanding] = useState(false);
  const { toast } = useToast();
  // Guided tour: auto-opens once per person, replayable from the header.
  const tour = useFeatureTour(QMS_DOCS_TOUR_ID, { enabled: !loading });

  useEffect(() => {
    Promise.all([
      api.get('qms/documents?status=live'),
      api.get('qms/accreditations'),
    ]).then(([d, a]) => {
      setDocs(d || []);
      setAccreditations(a || []);
    }).catch(() => {
      toast('Failed to load QMS data', 'error');
    }).finally(() => setLoading(false));
  }, []);

  const handleAcknowledge = (docId) => {
    setDocs(prev => prev.map(d =>
      d.id === docId ? { ...d, my_read: 1, my_read_at: new Date().toISOString() } : d
    ));
  };

  // Progress stats
  const requiredDocs = useMemo(() => docs.filter(d => d.requires_acknowledgement && d.status === 'live'), [docs]);
  const acknowledgedCount = useMemo(() => requiredDocs.filter(d => d.my_read).length, [requiredDocs]);
  const progressPct = requiredDocs.length ? Math.round((acknowledgedCount / requiredDocs.length) * 100) : 100;

  // Docs for active category, grouped by doc_type
  const categoryDocs = useMemo(() =>
    activeCategory === 'all' ? docs : docs.filter(d => d.category === activeCategory),
    [docs, activeCategory]);
  const grouped = useMemo(() => {
    const g = {};
    for (const d of categoryDocs) {
      if (!g[d.doc_type]) g[d.doc_type] = [];
      g[d.doc_type].push(d);
    }
    return g;
  }, [categoryDocs]);

  // Accreditation lookup by type
  const accByType = useMemo(() => {
    const m = {};
    for (const a of accreditations) m[a.type] = a;
    return m;
  }, [accreditations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[11px] text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-navy">Quality &amp; Compliance Library</h1>
          <p className="text-[11px] text-gray-500 mt-0.5">Company policies, processes, procedures and governance documents</p>
        </div>
        <button onClick={tour.start} className="text-[10px] text-gray-500 hover:text-navy border border-gray-200 hover:border-navy rounded-lg px-2.5 py-1 transition-colors" title="A one-minute tour of the library">
          Take the tour
        </button>
      </div>
      {tour.open && <FeatureTour steps={qmsDocsTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}

      {/* Accreditation strip */}
      <div>
        <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">Certifications &amp; Accreditations</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {KNOWN_ACCREDITATIONS.map(meta => (
            <AccreditationCard key={meta.type} acc={accByType[meta.type] || null} meta={meta} />
          ))}
        </div>
      </div>

      {/* Reading progress */}
      {requiredDocs.length > 0 && (
        <div className="bg-navy/5 border border-navy/10 rounded-xl px-4 py-3" data-tour="qms-progress">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[11px] font-bold text-navy">My Reading Progress</div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {acknowledgedCount} of {requiredDocs.length} required documents acknowledged
              </div>
            </div>
            <div className="text-[11px] font-bold text-navy">{progressPct}%</div>
          </div>
          <div className="h-2 bg-navy/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gold rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {acknowledgedCount < requiredDocs.length && (
            <button
              className="mt-2 text-[9px] text-gold hover:text-gold/70 font-semibold"
              onClick={() => {
                setViewMode('library');
                setShowOutstanding(true);
              }}
            >
              Jump to unread ({requiredDocs.length - acknowledgedCount} outstanding)
            </button>
          )}
        </div>
      )}

      {/* View toggle */}
      <div className="flex gap-1 flex-wrap">
        <button
          onClick={() => { setViewMode('library'); setShowOutstanding(false); }}
          className={`px-3 py-1.5 text-[11px] font-semibold rounded-l-lg border transition-colors ${
            viewMode === 'library' && !showOutstanding
              ? 'bg-navy text-white border-navy'
              : 'border-gray-300 text-gray-500 hover:text-navy hover:border-navy'
          }`}
        >
          Document Library
        </button>
        <button
          onClick={() => { setViewMode('matrix'); setShowOutstanding(false); }}
          className={`px-3 py-1.5 text-[11px] font-semibold border transition-colors ${
            viewMode === 'matrix'
              ? 'bg-navy text-white border-navy'
              : 'border-gray-300 text-gray-500 hover:text-navy hover:border-navy'
          }`}
        >
          Compliance Matrix
        </button>
        {requiredDocs.length > acknowledgedCount && (
          <button
            onClick={() => { setViewMode('library'); setShowOutstanding(v => !v); }}
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-r-lg border transition-colors ${
              showOutstanding
                ? 'bg-amber-500 text-amber-950 border-amber-500'
                : 'border-amber-300 text-amber-600 hover:border-amber-500 hover:text-amber-700'
            }`}
          >
            Outstanding ({requiredDocs.length - acknowledgedCount})
          </button>
        )}
      </div>

      {viewMode === 'matrix' ? (
        <MatrixView docs={docs} formatDate={formatDate} />
      ) : showOutstanding ? (
        /* Outstanding-only flat view */
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-semibold text-amber-700">
              Documents you still need to read &amp; acknowledge
            </div>
            <button
              onClick={() => setShowOutstanding(false)}
              className="text-[9px] text-gray-400 hover:text-gray-600 underline"
            >
              Show full library
            </button>
          </div>
          <div className="space-y-2">
            {requiredDocs.filter(d => !d.my_read).length === 0 ? (
              <div className="text-[11px] text-emerald-600 text-center py-8">All done — nothing outstanding!</div>
            ) : (
              requiredDocs.filter(d => !d.my_read).map(doc => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  accTypes={(doc.accreditation_types || '').split(',').filter(Boolean)}
                  onAcknowledge={(id) => { handleAcknowledge(id); if (requiredDocs.filter(d => !d.my_read && d.id !== id).length === 0) setShowOutstanding(false); }}
                />
              ))
            )}
          </div>
        </div>
      ) : (
        /* Category tabs + document groups */
        <div>
          <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 text-[11px] font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                activeCategory === 'all'
                  ? 'border-gold text-navy'
                  : 'border-transparent text-gray-500 hover:text-navy'
              }`}
            >
              All
              <span className="ml-1.5 text-[9px] text-gray-400">({docs.length})</span>
            </button>
            {CATEGORIES.map(cat => {
              const catDocs = docs.filter(d => d.category === cat.key);
              if (catDocs.length === 0) return null;
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`px-4 py-2 text-[11px] font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                    activeCategory === cat.key
                      ? 'border-gold text-navy'
                      : 'border-transparent text-gray-500 hover:text-navy'
                  }`}
                >
                  {cat.label}
                  <span className="ml-1.5 text-[9px] text-gray-400">({catDocs.length})</span>
                </button>
              );
            })}
          </div>

          {/* Document groups */}
          <div className="mt-4 space-y-6">
            {categoryDocs.length === 0 ? (
              <div className="text-[11px] text-gray-400 text-center py-8">No documents in this category yet.</div>
            ) : (
              QMS_DOC_TYPES.filter(t => grouped[t]?.length > 0).map(docType => (
                <div key={docType}>
                  <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {docTypePlural(docType)}
                  </div>
                  <div className="space-y-2">
                    {grouped[docType].map(doc => (
                      <DocumentRow
                        key={doc.id}
                        doc={doc}
                        accTypes={(doc.accreditation_types || '').split(',').filter(Boolean)}
                        onAcknowledge={handleAcknowledge}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
